import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  type FetchCache,
} from '@/store/fetchCache'

import { agentSettingsService } from '../services/agentSettingsService.http'
import {
  agentSettingsKeyOf,
  type AgentSettingsDraft,
  type AgentSettingsClientScope,
  type AgentSettingsDryRunResult,
  type AgentSettingsDraftWrite,
  type AgentSettingsEntry,
  type AgentSettingsFeatureError,
  type AgentSettingsKey,
  type AgentSettingsPublishInput,
  type AgentSettingsVersion,
  type ConnectorPolicyView,
  type DryRunEligibility,
  type FirmEffectiveAgentSettings,
} from '../types'

export type AgentSettingsOperation =
  | 'idle'
  | 'saving'
  | 'dry-running'
  | 'publishing'

const emptyEntry = (): AgentSettingsEntry => ({
  draft: null,
  effective: null,
  versions: [],
  dryRun: null,
  eligibility: null,
})
const EMPTY_ENTRY: AgentSettingsEntry = emptyEntry()
const EMPTY_CLIENT_SCOPES: AgentSettingsClientScope[] = []

const OPERATIONS: ReadonlySet<string> = new Set<
  AgentSettingsFeatureError['operation']
>([
  'listDrafts',
  'listVersions',
  'firmEffective',
  'connectorPolicy',
  'saveDraft',
  'dryRun',
  'publish',
])

/**
 * A stored feature error carries `code`, not `backendKey`; `isApiError` still
 * accepts it (status + message), so re-wrapping one would silently drop the
 * code. Recognise it first and keep it whole.
 */
const isFeatureError = (error: unknown): error is AgentSettingsFeatureError =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { operation?: unknown }).operation === 'string' &&
  OPERATIONS.has((error as { operation: string }).operation) &&
  typeof (error as { status?: unknown }).status === 'number' &&
  typeof (error as { message?: unknown }).message === 'string'

const errorFor = (
  error: unknown,
  operation: AgentSettingsFeatureError['operation']
): AgentSettingsFeatureError => {
  if (isFeatureError(error)) return { ...error, operation }
  const apiError = isApiError(error) ? error : toApiError(error)
  return {
    operation,
    status: apiError.status,
    code: apiError.backendKey,
    message: apiError.message,
    details: apiError.details,
  }
}

const draftFor = (
  drafts: readonly AgentSettingsDraft[],
  key: AgentSettingsKey
): AgentSettingsDraft | null =>
  drafts.find(
    (draft) =>
      draft.scope === key.scope &&
      draft.scopeKey === key.scopeKey &&
      draft.phase === key.phase &&
      draft.agentKey === key.agentKey
  ) ?? null

export const agentSettingsDraftWriteFor = (
  draft: AgentSettingsDraft
): AgentSettingsDraftWrite => ({
  scope: draft.scope,
  ...(draft.scopeKey === undefined ? {} : { scopeKey: draft.scopeKey }),
  phase: draft.phase,
  agentKey: draft.agentKey,
  baseVersion: draft.draftVersion,
  ...(draft.autonomyRung === undefined
    ? {}
    : { autonomyRung: draft.autonomyRung }),
  ...(draft.costCeilingPerRunMinor === undefined
    ? {}
    : { costCeilingPerRunMinor: draft.costCeilingPerRunMinor }),
  ...(draft.costCeilingPerPhaseMinor === undefined
    ? {}
    : { costCeilingPerPhaseMinor: draft.costCeilingPerPhaseMinor }),
  ...(draft.currency === undefined ? {} : { currency: draft.currency }),
  ...(draft.policyPresetKey === undefined
    ? {}
    : { policyPresetKey: draft.policyPresetKey }),
  ...(draft.reviewPolicy === undefined
    ? {}
    : { reviewPolicy: draft.reviewPolicy }),
  ...(draft.clientOverrideReason === undefined
    ? {}
    : { clientOverrideReason: draft.clientOverrideReason }),
  ...(draft.outputPermissions === undefined
    ? {}
    : { outputPermissions: draft.outputPermissions }),
})

/** Fixed field order makes this an equality key only; it is never a server digest. */
export const agentSettingsFingerprint = (
  input: AgentSettingsDraftWrite
): string =>
  JSON.stringify({
    scope: input.scope,
    scopeKey: input.scopeKey ?? null,
    phase: input.phase,
    agentKey: input.agentKey,
    baseVersion: input.baseVersion,
    autonomyRung: input.autonomyRung ?? null,
    costCeilingPerRunMinor: input.costCeilingPerRunMinor ?? null,
    costCeilingPerPhaseMinor: input.costCeilingPerPhaseMinor ?? null,
    currency: input.currency ?? null,
    policyPresetKey: input.policyPresetKey ?? null,
    reviewPolicy:
      input.reviewPolicy == null
        ? null
        : {
            requiresNamedHumanMerge:
              input.reviewPolicy.requiresNamedHumanMerge ?? null,
            secondReviewerRule: input.reviewPolicy.secondReviewerRule ?? null,
            escalationHours: input.reviewPolicy.escalationHours ?? null,
          },
    clientOverrideReason: input.clientOverrideReason ?? null,
    outputPermissions: input.outputPermissions ?? null,
  })

const currentDraftError = (
  operation: 'dryRun' | 'publish',
  message: string
): AgentSettingsFeatureError => ({ operation, status: 0, message })

export interface AgentSettingsState extends FetchCache {
  requestedKey: string | null
  loadedKey: string | null
  activeRequestId: string | null
  entry: AgentSettingsEntry
  error: AgentSettingsFeatureError | null
  savingKey: string | null
  saveSucceededKey: string | null
  operation: AgentSettingsOperation
  operationKey: string | null
  operationRequestId: string | null
  operationError: AgentSettingsFeatureError | null
  operationErrorKey: string | null
  firmEffectiveByKey: Record<string, FirmEffectiveAgentSettings>
  firmEffectiveLoading: boolean
  firmEffectiveRequestKey: string | null
  firmEffectiveRequestId: string | null
  firmEffectiveError: AgentSettingsFeatureError | null
  connectorPolicy: ConnectorPolicyView | null
  connectorPolicyOrgId: string | null
  connectorPolicyLoading: boolean
  connectorPolicyRequestId: string | null
  connectorPolicyError: AgentSettingsFeatureError | null
  clientScopes: AgentSettingsClientScope[]
  clientScopesOrgId: string | null
  clientScopesLoading: boolean
  clientScopesRequestId: string | null
  clientScopesError: AgentSettingsFeatureError | null
}

const initialState: AgentSettingsState = {
  requestedKey: null,
  activeRequestId: null,
  entry: emptyEntry(),
  error: null,
  savingKey: null,
  saveSucceededKey: null,
  operation: 'idle',
  operationKey: null,
  operationRequestId: null,
  operationError: null,
  operationErrorKey: null,
  firmEffectiveByKey: {},
  firmEffectiveLoading: false,
  firmEffectiveRequestKey: null,
  firmEffectiveRequestId: null,
  firmEffectiveError: null,
  connectorPolicy: null,
  connectorPolicyOrgId: null,
  connectorPolicyLoading: false,
  connectorPolicyRequestId: null,
  connectorPolicyError: null,
  clientScopes: [],
  clientScopesOrgId: null,
  clientScopesLoading: false,
  clientScopesRequestId: null,
  clientScopesError: null,
  ...initialFetchCache,
}

interface LoadedSettings {
  draft: AgentSettingsDraft | null
  effective: FirmEffectiveAgentSettings
  versions: AgentSettingsVersion[]
}

export interface FirmEffectiveRequest {
  orgId: string
  phase: AgentSettingsKey['phase']
  agentKey: string
}

export const firmEffectiveKeyOf = (request: FirmEffectiveRequest): string =>
  [request.orgId, request.phase, request.agentKey].join('|')

const firmEffectiveCollectionKeyOf = (
  requests: readonly FirmEffectiveRequest[]
): string => requests.map(firmEffectiveKeyOf).join(',')

/** Loads the exact published FIRM endpoint — never a project or mutable draft. */
export const loadFirmEffectiveCollection = createAsyncThunk<
  FirmEffectiveAgentSettings[],
  { requests: readonly FirmEffectiveRequest[] },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/loadFirmEffectiveCollection',
  async ({ requests }, { rejectWithValue }) => {
    try {
      return await Promise.all(
        requests.map(({ orgId, phase, agentKey }) =>
          agentSettingsService.getFirmPublishedEffective(orgId, phase, agentKey)
        )
      )
    } catch (error) {
      return rejectWithValue(errorFor(error, 'firmEffective'))
    }
  },
  {
    condition: ({ requests }, { getState }) => {
      const state = (getState() as { agentSettings: AgentSettingsState })
        .agentSettings
      const key = firmEffectiveCollectionKeyOf(requests)
      return (
        !state.firmEffectiveLoading && state.firmEffectiveRequestKey !== key
      )
    },
  }
)

/** Loads draft, immutable history, and the published FIRM-effective policy. */
export const openAgentSettings = createAsyncThunk<
  LoadedSettings,
  AgentSettingsKey,
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/open',
  async (key, { rejectWithValue }) => {
    try {
      const [drafts, versions, effective] = await Promise.all([
        agentSettingsService.listDrafts(key.orgId, key.scope, key.scopeKey),
        agentSettingsService.listVersions(
          key.orgId,
          key.scope,
          key.scopeKey,
          key.phase,
          key.agentKey
        ),
        agentSettingsService.getFirmPublishedEffective(
          key.orgId,
          key.phase,
          key.agentKey
        ),
      ])
      return { draft: draftFor(drafts, key), effective, versions }
    } catch (error) {
      return rejectWithValue(errorFor(error, 'firmEffective'))
    }
  },
  {
    condition: (key, { getState }) => {
      const state = (getState() as { agentSettings: AgentSettingsState })
        .agentSettings
      const cacheKey = agentSettingsKeyOf(key)
      if (state.requestedKey !== null && state.requestedKey !== cacheKey)
        return true
      return (
        !state.loading && (state.invalidated || state.loadedKey !== cacheKey)
      )
    },
  }
)

/** Loads the immutable organisation connector policy through the shared boundary. */
export const openConnectorPolicy = createAsyncThunk<
  ConnectorPolicyView,
  { orgId: string },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/openConnectorPolicy',
  async ({ orgId }, { rejectWithValue }) => {
    try {
      return await agentSettingsService.getConnectorPolicy(orgId)
    } catch (error) {
      return rejectWithValue(errorFor(error, 'connectorPolicy'))
    }
  },
  {
    condition: ({ orgId }, { getState }) => {
      const state = (getState() as { agentSettings: AgentSettingsState })
        .agentSettings
      return (
        !state.connectorPolicyLoading && state.connectorPolicyOrgId !== orgId
      )
    },
  }
)

/** The server translates public client rows to the opaque keys the ladder resolves. */
export const openAgentSettingsClientScopes = createAsyncThunk<
  AgentSettingsClientScope[],
  { orgId: string },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/openClientScopes',
  async ({ orgId }, { rejectWithValue }) => {
    try {
      return await agentSettingsService.listClientScopes(orgId)
    } catch (error) {
      return rejectWithValue(errorFor(error, 'listDrafts'))
    }
  },
  {
    condition: ({ orgId }, { getState }) => {
      const state = (getState() as { agentSettings: AgentSettingsState })
        .agentSettings
      return !state.clientScopesLoading && state.clientScopesOrgId !== orgId
    },
  }
)

/**
 * Re-reads the authoritative entry after a successful write. Never throws and
 * never re-throws the reread's outcome into the calling mutation: a failure is
 * recorded by openAgentSettings.rejected as the list-level error.
 */
const rereadAfterWrite = async (
  api: {
    dispatch: (action: unknown) => unknown
    getState: () => unknown
  },
  key: AgentSettingsKey
): Promise<void> => {
  const state = (api.getState() as { agentSettings: AgentSettingsState })
    .agentSettings
  if (state.requestedKey !== agentSettingsKeyOf(key)) return
  api.dispatch(agentSettingsSlice.actions.agentSettingsEntryInvalidated())
  await api.dispatch(openAgentSettings(key))
}

/** One mutation at a time per settings key; another key is never blocked. */
const operationAvailableFor = (
  { key }: { key: AgentSettingsKey },
  { getState }: { getState: () => unknown }
): boolean => {
  const state = (getState() as { agentSettings: AgentSettingsState })
    .agentSettings
  return (
    state.operation === 'idle' ||
    state.operationKey !== agentSettingsKeyOf(key)
  )
}

export const saveAgentSettingsDraft = createAsyncThunk<
  void,
  { key: AgentSettingsKey; input: AgentSettingsDraftWrite },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/saveDraft',
  async ({ key, input }, api) => {
    try {
      await agentSettingsService.saveDraft(key.orgId, input)
    } catch (error) {
      return api.rejectWithValue(errorFor(error, 'saveDraft'))
    }
    // The write succeeded, so the save fulfils whatever the reread does. A
    // reread failure is owned by openAgentSettings.rejected, which lands it on
    // the list-level channel with its original status/code/message.
    await rereadAfterWrite(api, key)
  },
  { condition: operationAvailableFor }
)

export const dryRunAgentSettings = createAsyncThunk<
  AgentSettingsDryRunResult,
  {
    key: AgentSettingsKey
    input: AgentSettingsDraftWrite
    fingerprint: string
  },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/dryRun',
  async ({ key, input, fingerprint }, api) => {
    const state = (api.getState() as { agentSettings: AgentSettingsState })
      .agentSettings
    const settingsKey = agentSettingsKeyOf(key)
    const draft = state.loadedKey === settingsKey ? state.entry.draft : null
    if (draft === null)
      return api.rejectWithValue(
        currentDraftError(
          'dryRun',
          'A saved current draft is required for dry run.'
        )
      )

    const canonicalInput = agentSettingsDraftWriteFor(draft)
    const canonicalFingerprint = agentSettingsFingerprint(canonicalInput)
    if (
      fingerprint !== canonicalFingerprint ||
      agentSettingsFingerprint(input) !== canonicalFingerprint
    )
      return api.rejectWithValue(
        currentDraftError(
          'dryRun',
          'Save or reconcile edits before running a dry run for this draft.'
        )
      )

    try {
      return await agentSettingsService.dryRun(key.orgId, canonicalInput)
    } catch (error) {
      return api.rejectWithValue(errorFor(error, 'dryRun'))
    }
  },
  { condition: operationAvailableFor }
)

export const publishAgentSettings = createAsyncThunk<
  AgentSettingsVersion,
  { key: AgentSettingsKey; fingerprint: string },
  { rejectValue: AgentSettingsFeatureError }
>(
  'agentSettings/publish',
  async ({ key, fingerprint }, api) => {
    const state = (api.getState() as { agentSettings: AgentSettingsState })
      .agentSettings
    const settingsKey = agentSettingsKeyOf(key)
    const draft = state.loadedKey === settingsKey ? state.entry.draft : null
    const eligibility = state.entry.eligibility
    if (draft === null || eligibility === null)
      return api.rejectWithValue(
        currentDraftError(
          'publish',
          'A current server dry run is required before publishing.'
        )
      )

    const canonicalInput = agentSettingsDraftWriteFor(draft)
    const canonicalFingerprint = agentSettingsFingerprint(canonicalInput)
    if (
      fingerprint !== canonicalFingerprint ||
      eligibility.settingsKey !== settingsKey ||
      eligibility.draftFingerprint !== canonicalFingerprint ||
      eligibility.candidateDigest.trim() === ''
    )
      return api.rejectWithValue(
        currentDraftError(
          'publish',
          'The current draft no longer matches the successful dry run.'
        )
      )

    const input: AgentSettingsPublishInput = {
      scope: key.scope,
      ...(key.scopeKey === undefined ? {} : { scopeKey: key.scopeKey }),
      phase: key.phase,
      agentKey: key.agentKey,
      baseVersion: draft.draftVersion,
      expectedDigest: eligibility.candidateDigest,
    }
    let version: AgentSettingsVersion
    try {
      version = await agentSettingsService.publish(key.orgId, input)
    } catch (error) {
      return api.rejectWithValue(errorFor(error, 'publish'))
    }
    // Published is published: the reread cannot turn it into a refusal.
    await rereadAfterWrite(api, key)
    return version
  },
  { condition: operationAvailableFor }
)

const resetOperation = (state: AgentSettingsState): void => {
  state.operation = 'idle'
  state.operationKey = null
  state.operationRequestId = null
  state.savingKey = null
}

const isCurrentOperation = (
  state: AgentSettingsState,
  requestId: string,
  key: string
): boolean =>
  state.operationRequestId === requestId &&
  state.operationKey === key &&
  state.requestedKey === key

const startOperation = (
  state: AgentSettingsState,
  operation: Exclude<AgentSettingsOperation, 'idle'>,
  key: string,
  requestId: string
): void => {
  state.operation = operation
  state.operationKey = key
  state.operationRequestId = requestId
  state.operationError = null
  state.operationErrorKey = null
}

const failOperation = (
  state: AgentSettingsState,
  error: AgentSettingsFeatureError,
  key: string
): void => {
  // Operation refusals stay on their own channel; `error` is the list-level
  // read error only, so a page can always offer its reread retry.
  resetOperation(state)
  state.operationError = error
  state.operationErrorKey = key
}

export const agentSettingsSlice = createSlice({
  name: 'agentSettings',
  initialState,
  reducers: {
    agentSettingsClosed(state) {
      state.requestedKey = null
      state.loadedKey = null
      state.activeRequestId = null
      state.entry = emptyEntry()
      state.error = null
      state.savingKey = null
      state.saveSucceededKey = null
      resetOperation(state)
      state.operationError = null
      state.operationErrorKey = null
      state.firmEffectiveByKey = {}
      state.firmEffectiveLoading = false
      state.firmEffectiveRequestKey = null
      state.firmEffectiveRequestId = null
      state.firmEffectiveError = null
      state.connectorPolicy = null
      state.connectorPolicyOrgId = null
      state.connectorPolicyLoading = false
      state.connectorPolicyRequestId = null
      state.connectorPolicyError = null
      state.clientScopes = []
      state.clientScopesOrgId = null
      state.clientScopesLoading = false
      state.clientScopesRequestId = null
      state.clientScopesError = null
      invalidate(state)
    },
    agentSettingsEntryInvalidated(state) {
      invalidate(state)
      state.saveSucceededKey = null
      state.entry.eligibility = null
    },
    agentSettingsErrorDismissed(state) {
      state.error = null
      state.operationError = null
      state.operationErrorKey = null
    },
    connectorPolicyRefetchArmed(state) {
      state.connectorPolicy = null
      state.connectorPolicyOrgId = null
      state.connectorPolicyLoading = false
      state.connectorPolicyRequestId = null
      state.connectorPolicyError = null
    },
    clientScopesRefetchArmed(state) {
      state.clientScopes = []
      state.clientScopesOrgId = null
      state.clientScopesLoading = false
      state.clientScopesRequestId = null
      state.clientScopesError = null
    },
    firmEffectiveRefetchArmed(state) {
      state.firmEffectiveLoading = false
      state.firmEffectiveRequestKey = null
      state.firmEffectiveRequestId = null
      state.firmEffectiveError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(openAgentSettings.pending, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg)
        if (state.requestedKey !== key) {
          state.entry = emptyEntry()
          state.loadedKey = null
          resetOperation(state)
        }
        fetchStarted(state)
        state.requestedKey = key
        state.activeRequestId = action.meta.requestId
        state.error = null
      })
      .addCase(openAgentSettings.fulfilled, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg)
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedKey !== key
        )
          return
        state.entry = { ...action.payload, dryRun: null, eligibility: null }
        state.firmEffectiveByKey[
          firmEffectiveKeyOf({
            orgId: action.meta.arg.orgId,
            phase: action.meta.arg.phase,
            agentKey: action.meta.arg.agentKey,
          })
        ] = action.payload.effective
        state.loadedKey = key
        fetchSucceeded(state, key)
      })
      .addCase(openAgentSettings.rejected, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg)
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedKey !== key
        )
          return
        fetchFailed(state)
        state.error = action.payload ?? errorFor(action.error, 'listDrafts')
      })
      .addCase(openConnectorPolicy.pending, (state, action) => {
        state.connectorPolicy = null
        state.connectorPolicyOrgId = action.meta.arg.orgId
        state.connectorPolicyLoading = true
        state.connectorPolicyRequestId = action.meta.requestId
        state.connectorPolicyError = null
      })
      .addCase(openConnectorPolicy.fulfilled, (state, action) => {
        if (
          state.connectorPolicyRequestId !== action.meta.requestId ||
          state.connectorPolicyOrgId !== action.meta.arg.orgId
        )
          return
        state.connectorPolicy = action.payload
        state.connectorPolicyLoading = false
      })
      .addCase(openConnectorPolicy.rejected, (state, action) => {
        if (
          state.connectorPolicyRequestId !== action.meta.requestId ||
          state.connectorPolicyOrgId !== action.meta.arg.orgId
        )
          return
        state.connectorPolicyLoading = false
        state.connectorPolicyError =
          action.payload ?? errorFor(action.error, 'connectorPolicy')
      })
      .addCase(openAgentSettingsClientScopes.pending, (state, action) => {
        state.clientScopes = []
        state.clientScopesOrgId = action.meta.arg.orgId
        state.clientScopesLoading = true
        state.clientScopesRequestId = action.meta.requestId
        state.clientScopesError = null
      })
      .addCase(openAgentSettingsClientScopes.fulfilled, (state, action) => {
        if (
          state.clientScopesRequestId !== action.meta.requestId ||
          state.clientScopesOrgId !== action.meta.arg.orgId
        )
          return
        state.clientScopes = action.payload
        state.clientScopesLoading = false
      })
      .addCase(openAgentSettingsClientScopes.rejected, (state, action) => {
        if (
          state.clientScopesRequestId !== action.meta.requestId ||
          state.clientScopesOrgId !== action.meta.arg.orgId
        )
          return
        state.clientScopesLoading = false
        state.clientScopesError =
          action.payload ?? errorFor(action.error, 'listDrafts')
      })
      .addCase(loadFirmEffectiveCollection.pending, (state, action) => {
        state.firmEffectiveLoading = true
        state.firmEffectiveRequestKey = firmEffectiveCollectionKeyOf(
          action.meta.arg.requests
        )
        state.firmEffectiveRequestId = action.meta.requestId
        state.firmEffectiveError = null
      })
      .addCase(loadFirmEffectiveCollection.fulfilled, (state, action) => {
        if (state.firmEffectiveRequestId !== action.meta.requestId) return
        const requests = action.meta.arg.requests
        for (const [index, effective] of action.payload.entries()) {
          const request = requests[index]
          if (request !== undefined)
            state.firmEffectiveByKey[firmEffectiveKeyOf(request)] = effective
        }
        state.firmEffectiveLoading = false
      })
      .addCase(loadFirmEffectiveCollection.rejected, (state, action) => {
        if (state.firmEffectiveRequestId !== action.meta.requestId) return
        state.firmEffectiveLoading = false
        state.firmEffectiveError =
          action.payload ?? errorFor(action.error, 'firmEffective')
      })
      .addCase(saveAgentSettingsDraft.pending, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        state.savingKey = key
        state.saveSucceededKey = null
        state.entry.eligibility = null
        startOperation(state, 'saving', key, action.meta.requestId)
      })
      .addCase(saveAgentSettingsDraft.fulfilled, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        resetOperation(state)
        state.saveSucceededKey = key
      })
      .addCase(saveAgentSettingsDraft.rejected, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        failOperation(
          state,
          action.payload ?? errorFor(action.error, 'saveDraft'),
          key
        )
      })
      .addCase(dryRunAgentSettings.pending, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        state.entry.eligibility = null
        startOperation(state, 'dry-running', key, action.meta.requestId)
      })
      .addCase(dryRunAgentSettings.fulfilled, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        const currentDraft = state.loadedKey === key ? state.entry.draft : null
        const currentFingerprint =
          currentDraft === null
            ? null
            : agentSettingsFingerprint(agentSettingsDraftWriteFor(currentDraft))
        if (
          currentFingerprint === null ||
          currentFingerprint !== action.meta.arg.fingerprint ||
          action.payload.candidateDigest.trim() === ''
        ) {
          state.entry.dryRun = null
          state.entry.eligibility = null
          resetOperation(state)
          return
        }
        state.entry.dryRun = action.payload
        state.entry.eligibility = {
          settingsKey: key,
          draftFingerprint: currentFingerprint,
          candidateDigest: action.payload.candidateDigest,
          noChange: action.payload.noChange,
        }
        resetOperation(state)
      })
      .addCase(dryRunAgentSettings.rejected, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        state.entry.dryRun = null
        state.entry.eligibility = null
        failOperation(
          state,
          action.payload ?? errorFor(action.error, 'dryRun'),
          key
        )
      })
      .addCase(publishAgentSettings.pending, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        startOperation(state, 'publishing', key, action.meta.requestId)
      })
      .addCase(publishAgentSettings.fulfilled, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        state.entry.eligibility = null
        resetOperation(state)
      })
      .addCase(publishAgentSettings.rejected, (state, action) => {
        const key = agentSettingsKeyOf(action.meta.arg.key)
        if (!isCurrentOperation(state, action.meta.requestId, key)) return
        state.entry.eligibility = null
        failOperation(
          state,
          action.payload ?? errorFor(action.error, 'publish'),
          key
        )
      })
  },
})

export const {
  agentSettingsClosed,
  agentSettingsEntryInvalidated,
  agentSettingsErrorDismissed,
  connectorPolicyRefetchArmed,
  clientScopesRefetchArmed,
  firmEffectiveRefetchArmed,
} = agentSettingsSlice.actions

interface WithAgentSettings {
  agentSettings: AgentSettingsState
}

export const selectAgentSettingsEntry = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsEntry =>
  state.agentSettings.loadedKey === agentSettingsKeyOf(key)
    ? state.agentSettings.entry
    : EMPTY_ENTRY
export const selectSelectedDraft = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsDraft | null => selectAgentSettingsEntry(state, key).draft
export const selectFirmEffective = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): FirmEffectiveAgentSettings | null =>
  selectAgentSettingsEntry(state, key).effective
export const selectFirmEffectiveByRequest = (
  state: WithAgentSettings,
  request: FirmEffectiveRequest
): FirmEffectiveAgentSettings | null =>
  state.agentSettings.firmEffectiveByKey[firmEffectiveKeyOf(request)] ?? null
export const selectFirmEffectiveMap = (
  state: WithAgentSettings
): Record<string, FirmEffectiveAgentSettings> =>
  state.agentSettings.firmEffectiveByKey
export const selectFirmEffectiveLoading = (state: WithAgentSettings): boolean =>
  state.agentSettings.firmEffectiveLoading
export const selectFirmEffectiveError = (
  state: WithAgentSettings
): AgentSettingsFeatureError | null => state.agentSettings.firmEffectiveError
export const selectAgentSettingsVersions = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsVersion[] => selectAgentSettingsEntry(state, key).versions
export const selectLatestPublishedVersion = createSelector(
  [selectAgentSettingsVersions],
  (versions) =>
    versions.reduce<AgentSettingsVersion | null>(
      (latest, version) =>
        latest === null || version.version > latest.version ? version : latest,
      null
    )
)
export const selectAgentSettingsLoading = (state: WithAgentSettings): boolean =>
  state.agentSettings.loading
/** True once the entry for this exact key has been read at least once. */
export const selectAgentSettingsLoadedFor = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): boolean => state.agentSettings.loadedKey === agentSettingsKeyOf(key)
export const selectAgentSettingsError = (
  state: WithAgentSettings
): AgentSettingsFeatureError | null => state.agentSettings.error
export const selectAgentSettingsSaving = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): boolean => state.agentSettings.savingKey === agentSettingsKeyOf(key)
export const selectConnectorPolicy = (
  state: WithAgentSettings,
  orgId: string
): ConnectorPolicyView | null =>
  state.agentSettings.connectorPolicyOrgId === orgId
    ? state.agentSettings.connectorPolicy
    : null
export const selectConnectorPolicyLoading = (
  state: WithAgentSettings,
  orgId: string
): boolean =>
  state.agentSettings.connectorPolicyOrgId === orgId &&
  state.agentSettings.connectorPolicyLoading
export const selectConnectorPolicyError = (
  state: WithAgentSettings,
  orgId: string
): AgentSettingsFeatureError | null =>
  state.agentSettings.connectorPolicyOrgId === orgId
    ? state.agentSettings.connectorPolicyError
    : null
export const selectAgentSettingsClientScopes = (
  state: WithAgentSettings,
  orgId: string
): AgentSettingsClientScope[] =>
  state.agentSettings.clientScopesOrgId === orgId
    ? state.agentSettings.clientScopes
    : EMPTY_CLIENT_SCOPES
export const selectAgentSettingsClientScopesLoading = (
  state: WithAgentSettings,
  orgId: string
): boolean =>
  state.agentSettings.clientScopesOrgId === orgId &&
  state.agentSettings.clientScopesLoading
export const selectAgentSettingsClientScopesError = (
  state: WithAgentSettings,
  orgId: string
): AgentSettingsFeatureError | null =>
  state.agentSettings.clientScopesOrgId === orgId
    ? state.agentSettings.clientScopesError
    : null
export const selectDryRunResult = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsDryRunResult | null =>
  selectAgentSettingsEntry(state, key).dryRun
export const selectDryRunEligibility = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): DryRunEligibility | null => selectAgentSettingsEntry(state, key).eligibility
export const selectCanPublish = (
  state: WithAgentSettings,
  key: AgentSettingsKey,
  currentFingerprint: string
): boolean => {
  const entry = selectAgentSettingsEntry(state, key)
  const eligibility = entry.eligibility
  return (
    entry.draft !== null &&
    state.agentSettings.operation === 'idle' &&
    eligibility !== null &&
    eligibility.settingsKey === agentSettingsKeyOf(key) &&
    eligibility.draftFingerprint === currentFingerprint &&
    eligibility.candidateDigest.trim() !== '' &&
    eligibility.noChange === false
  )
}
export const selectAgentSettingsOperation = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsOperation =>
  state.agentSettings.operationKey === agentSettingsKeyOf(key)
    ? state.agentSettings.operation
    : 'idle'
export const selectAgentSettingsOperationError = (
  state: WithAgentSettings,
  key: AgentSettingsKey
): AgentSettingsFeatureError | null =>
  state.agentSettings.operationErrorKey === agentSettingsKeyOf(key)
    ? state.agentSettings.operationError
    : null
